import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Toast from "../Toast/Toast";
import { apiFetch, readJsonSafe } from "../config/apiClient";
import {
  clearAuth,
  getUser,
  isAuthenticated,
  normalizeAuthPayload,
  setAuthSession,
} from "../config/auth";
import "./login.css";

const OTP_LENGTH = 6;
const OTP_COOLDOWN_SECONDS = 60;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GOOGLE_IDENTITY_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const mapForgotPasswordError = (message) => {
  const text = String(message || "").trim();
  if (!text) return "Something went wrong. Please try again.";
  if (/please wait 60 seconds/i.test(text)) return "Please wait 60 seconds before requesting another OTP";
  if (/request limit exceeded/i.test(text)) return "Too many OTP requests. Please try again later.";
  if (/user not found/i.test(text)) return "No account found with this email.";
  if (/invalid otp/i.test(text)) return "Invalid OTP";
  if (/otp has expired/i.test(text)) return "OTP has expired";
  return text;
};

function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);
  const [emailId, setEmailId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState("");

  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotStep, setForgotStep] = useState("email");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotOtpError, setForgotOtpError] = useState("");
  const [forgotBanner, setForgotBanner] = useState("");
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);

  useEffect(() => {
    if (!isAuthenticated()) return;
    const role = String(getUser()?.role || "STUDENT").toUpperCase();
    navigate(role === "ADMIN" ? "/admin/dashboard" : "/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const loginMessage = String(location.state?.loginMessage || "").trim();
    if (!loginMessage) return;
    setToastMessage(loginMessage);
  }, [location.state]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return undefined;
    const timerId = window.setTimeout(() => {
      setResendCooldownSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => window.clearTimeout(timerId);
  }, [resendCooldownSeconds]);

  const normalizedForgotEmail = useMemo(
    () => forgotEmail.trim().toLowerCase(),
    [forgotEmail]
  );
  const canResetPassword =
    /^\d{6}$/.test(forgotOtp.trim()) && Boolean(newPassword.trim());

  const completeLogin = useCallback((data, fallbackEmail = "") => {
    const { token, user } = normalizeAuthPayload(data, {
      username: fallbackEmail,
      email: fallbackEmail,
      subscriptionPlan: data?.user?.subscriptionPlan || "FREE",
      blocked: data?.user?.blocked,
    });

    if (!token || !user?.email) {
      throw new Error("Login response is invalid. Please try again.");
    }

    clearAuth();
    setAuthSession({ token, user });
    setToastMessage("Login successful!");

    window.setTimeout(() => {
      const role = String(user?.role || "STUDENT").toUpperCase();
      const redirectTo = String(location.state?.redirectTo || "").trim();
      navigate(redirectTo || (role === "ADMIN" ? "/admin/dashboard" : "/dashboard"));
    }, 2000);
  }, [location.state?.redirectTo, navigate]);

  const resetForgotPasswordState = () => {
    setForgotMode(false);
    setForgotEmail("");
    setForgotOtp("");
    setNewPassword("");
    setForgotStep("email");
    setForgotLoading(false);
    setResetLoading(false);
    setForgotError("");
    setForgotOtpError("");
    setForgotBanner("");
    setResendCooldownSeconds(0);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const normalizedEmail = emailId.trim().toLowerCase();
    const normalizedPassword = password.trim();

    if (!normalizedEmail || !normalizedPassword) {
      setError("Email and password are required.");
      return;
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError("");
    setToastMessage("");

    try {
      const response = await apiFetch("/users/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailId: normalizedEmail, password: normalizedPassword }),
      });

      const data = await readJsonSafe(response);
      if (!response.ok) {
        throw new Error(data.message || "Invalid credentials");
      }

      completeLogin(data, normalizedEmail);
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    if (!credential) {
      setGoogleError("Google login failed. Please try again.");
      return;
    }

    setIsGoogleLoading(true);
    setGoogleError("");
    setError("");
    setToastMessage("");

    try {
      const response = await apiFetch("/users/login/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: credential }),
      });
      const data = await readJsonSafe(response);

      if (response.status === 403) {
        throw new Error(data?.message || "This account is blocked.");
      }

      if (!response.ok) {
        throw new Error(data?.message || "Unable to verify Google account.");
      }

      completeLogin(data, data?.user?.emailId || "");
    } catch (err) {
      setGoogleError(err.message || "Google login failed. Please try again.");
    } finally {
      setIsGoogleLoading(false);
    }
  }, [completeLogin]);

  useEffect(() => {
    if (forgotMode) return undefined;
    if (!GOOGLE_CLIENT_ID) {
      setIsGoogleReady(false);
      setGoogleError("Google Sign-In is not configured yet.");
      return undefined;
    }

    let cancelled = false;

    const renderGoogleButton = () => {
      if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => handleGoogleCredential(response?.credential),
      });

      googleButtonRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        shape: "pill",
        width: "360",
        text: "continue_with",
      });
      setIsGoogleReady(true);
      setGoogleError("");
    };

    const existingScript = document.querySelector(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`
    );

    if (existingScript && window.google?.accounts?.id) {
      renderGoogleButton();
      return () => {
        cancelled = true;
      };
    }

    const script = existingScript || document.createElement("script");
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;

    const handleLoad = () => {
      if (cancelled) return;
      renderGoogleButton();
    };

    const handleError = () => {
      if (cancelled) return;
      setIsGoogleReady(false);
      setGoogleError("Google login failed to load. Please try again.");
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    if (!existingScript) {
      document.body.appendChild(script);
    } else if (window.google?.accounts?.id) {
      handleLoad();
    }

    return () => {
      cancelled = true;
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, [forgotMode, handleGoogleCredential]);

  const sendForgotPasswordOtp = async () => {
    if (!normalizedForgotEmail) {
      setForgotError("Please enter your registered email.");
      return;
    }
    if (!EMAIL_REGEX.test(normalizedForgotEmail)) {
      setForgotError("Please enter a valid email address.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");
    setForgotOtpError("");
    setForgotBanner("");

    try {
      const response = await apiFetch("/users/forgot-password/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailId: normalizedForgotEmail }),
      });
      const data = await readJsonSafe(response);

      if (!response.ok) {
        const mapped = mapForgotPasswordError(data?.message);
        if (/please wait 60 seconds/i.test(mapped)) {
          setResendCooldownSeconds(OTP_COOLDOWN_SECONDS);
        }
        if (/Too many OTP requests/i.test(mapped)) {
          setResendCooldownSeconds(OTP_COOLDOWN_SECONDS);
          setForgotBanner(mapped);
          return;
        }
        throw new Error(mapped);
      }

      setForgotStep("reset");
      setResendCooldownSeconds(OTP_COOLDOWN_SECONDS);
      setToastMessage("Password reset OTP sent successfully.");
    } catch (err) {
      setForgotError(err.message || "Unable to send OTP right now.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (!/^\d{6}$/.test(forgotOtp.trim())) {
      setForgotOtpError("OTP should be exactly 6 digits.");
      return;
    }
    if (!newPassword.trim()) {
      setForgotError("Please enter a new password.");
      return;
    }

    setResetLoading(true);
    setForgotError("");
    setForgotOtpError("");

    try {
      const response = await apiFetch("/users/forgot-password/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailId: normalizedForgotEmail,
          otp: forgotOtp.trim(),
          newPassword,
        }),
      });
      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(mapForgotPasswordError(data?.message));
      }

      resetForgotPasswordState();
      setToastMessage("Password reset successful. Please login with your new password.");
    } catch (err) {
      const message = err.message || "Unable to reset password.";
      if (/Invalid OTP|OTP has expired/i.test(message)) {
        setForgotOtpError(message);
      } else {
        setForgotError(message);
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Helmet>
        <title>Login | AAMSchool</title>
        <link rel="canonical" href="https://www.aamschool.in/login" />
        <meta
          name="description"
          content="Log in to AAMSchool to access quizzes, dashboard features, and personalized exam preparation tools."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="auth-backdrop" />
      <div className="auth-shell">
        <section className="auth-side-panel">
          <Link to="/" className="auth-home-link">
            AAMSCHOOL
          </Link>
          <p className="auth-side-kicker">Secure Access</p>
          <h1>{forgotMode ? "Reset your password with OTP" : "Welcome back to your exam prep hub"}</h1>
          <p className="auth-side-copy">
            {forgotMode
              ? "Use your registered email, verify the one-time password, and set a fresh password without leaving the flow."
              : "Sign in through a focused modal experience and continue with quizzes, jobs, and exam updates in one place."}
          </p>
          <div className="auth-side-badges">
            <span>OTP secured</span>
            <span>Fast recovery</span>
            <span>Student ready</span>
          </div>
        </section>

        <div className="login-card auth-modal-card">
          <div className="auth-modal-topbar">
            <span className="auth-modal-pill">{forgotMode ? "Recovery" : "Sign In"}</span>
            <button type="button" className="auth-close-btn" onClick={() => navigate("/")}>
              Close
            </button>
          </div>

          <h2>{forgotMode ? "Forgot Password" : "Login"}</h2>

          {!forgotMode ? (
            <>
              <form onSubmit={handleLogin}>
                <div className="login-field">
                  <input
                    type="text"
                    placeholder="Enter Email"
                    value={emailId}
                    onChange={(event) => {
                      setEmailId(event.target.value);
                      setError("");
                    }}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="login-field">
                  <input
                    type="password"
                    placeholder="Enter Password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setError("");
                    }}
                    autoComplete="current-password"
                    required
                  />
                </div>

                {error ? <p className="login-error">{error}</p> : null}

                <button type="submit" disabled={loading} className="login-btn">
                  {loading ? "Logging in..." : "Login"}
                </button>
              </form>

              <div className="login-divider">
                <span>or</span>
              </div>

              {googleError ? <p className="login-error">{googleError}</p> : null}
              <div className={`google-login-shell ${isGoogleLoading ? "is-loading" : ""}`}>
                <div
                  ref={googleButtonRef}
                  className={`google-login-button ${!isGoogleReady ? "is-hidden" : ""}`}
                />
                {!isGoogleReady && !googleError ? (
                  <button type="button" className="login-btn login-btn-google" disabled>
                    Loading Google...
                  </button>
                ) : null}
                {isGoogleLoading ? (
                  <p className="login-google-note">Verifying Google account...</p>
                ) : null}
              </div>

              <button
                type="button"
                className="login-link-btn"
                onClick={() => {
                  setForgotMode(true);
                  setError("");
                }}
              >
                Forgot password?
              </button>

              <p className="login-footer-text">
                Don&apos;t have an account?{" "}
                <span onClick={() => navigate("/signup")}>Register</span>
              </p>
            </>
          ) : (
            <>
              {forgotBanner ? <p className="login-error">{forgotBanner}</p> : null}
              {forgotError ? <p className="login-error">{forgotError}</p> : null}

              <form onSubmit={handleResetPassword}>
                <div className="login-field">
                  <input
                    type="email"
                    placeholder="Registered Email"
                    value={forgotEmail}
                    onChange={(event) => {
                      setForgotEmail(event.target.value);
                      setForgotError("");
                      setForgotOtpError("");
                    }}
                    autoComplete="email"
                    readOnly={forgotStep === "reset"}
                    className={forgotStep === "reset" ? "is-locked" : ""}
                  />
                </div>

                <button
                  type="button"
                  className="login-btn login-btn-secondary"
                  onClick={sendForgotPasswordOtp}
                  disabled={forgotLoading || resendCooldownSeconds > 0}
                >
                  {forgotLoading
                    ? "Sending OTP..."
                    : resendCooldownSeconds > 0
                    ? `Resend OTP in ${resendCooldownSeconds}s`
                    : forgotStep === "email"
                    ? "Send OTP"
                    : "Resend OTP"}
                </button>

                {forgotStep === "reset" ? (
                  <>
                    <div className="login-field">
                      <input
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={forgotOtp}
                        onChange={(event) => {
                          setForgotOtp(event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH));
                          setForgotOtpError("");
                        }}
                        inputMode="numeric"
                        maxLength={OTP_LENGTH}
                      />
                    </div>
                    {forgotOtpError ? <p className="login-helper">{forgotOtpError}</p> : null}
                    <div className="login-field">
                      <input
                        type="password"
                        placeholder="Enter New Password"
                        value={newPassword}
                        onChange={(event) => {
                          setNewPassword(event.target.value);
                          setForgotError("");
                        }}
                        autoComplete="new-password"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={resetLoading || !canResetPassword}
                      className="login-btn"
                    >
                      {resetLoading ? "Resetting..." : "Reset Password"}
                    </button>
                  </>
                ) : null}
              </form>

              <button
                type="button"
                className="login-link-btn"
                onClick={resetForgotPasswordState}
              >
                Back to login
              </button>
            </>
          )}

          <Toast message={toastMessage} onClose={() => setToastMessage("")} duration={2500} />
        </div>
      </div>
    </div>
  );
}

export default Login;
